import { action, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { StringKeyMap } from '../../../adl-gen/common';
import { DbKey } from '../../../adl-gen/common/db';
import { AppUser, Skill } from '../../../adl-gen/totalinteraction/futurefit/db';
import { Competency } from '../../../adl-gen/totalinteraction/futurefit/frontend';
import { TranslateFn, withI18n, WithI18nProps } from '../../../internationalization/i18n-hoc';
import { i18next } from '../../../internationalization/i18next';
import { QuizResultsHelper } from '../../../stores/quiz-results-store';
import { MarkdownContent } from '../../widgets/markdown-content/markdown-content';
import { Pill } from '../../widgets/pill/pill';
import { GuidanceCategory } from '../guidance-category/guidance-category';
import { Modal } from '../modal/modal';
import { Tile } from '../tile/tile';

const styles = require('./competency-gap-report.css');

/// return a comparison function for sort - based on sort by result from provided accessor function
/// Return function sorts (a, b) L,R from LOWEST to HIGHEST (ASC)
/// SS-777 enhancement to deal with situation where skill gaps are the same - which results in a
/// stochastic but unpredictable order
/// Problem here is that the sign-flip code (see the function order()) will also flip any
/// alphanumeric sorting which we don't want...
///  T => generic type, N ~ number, S ~ string
function compareByGet<T, N, S>(get: (t: T) => N, get2: (t: T) => S, sign: Sign): (a: T, b: T) => number {
  return (a: T, b: T) => {
    const valA: N = get(a);
    const valB: N = get(b);
    const valA2: S = get2(a);
    const valB2: S = get2(b);
    /// Only use Sign on first compare
    return valA < valB ? sign * -1 : valA > valB ? sign * 1 : valA2 < valB2 ? -1 : valA2 > valB2 ? 1 : 0;
  };
}

/// FF2-406 rename 'worstFirst' and 'bestFirst' as below to make clear which sort direction they should be in
type Sign = -1 | 1;
const asc: Sign = 1;
const desc: Sign = -1;
const biggestGapFirst = desc;
const smallestGapFirst = asc;

// /// flip a compareBy function to use an order (ascending or descending)
// function order<T>(compareBy: (a: T, b: T) => number, sign: Sign) {
//   return (a: T, b: T) => {
//     return sign * compareBy(a, b);
//   };
// }

type DiffCategory = keyof typeof titlesByDiffCategory;
const diffCategoryList: DiffCategory[] = ['essential', 'desirable', 'supplementary'];

const titlesByDiffCategory = {
  supplementary: 'Continue',
  desirable: 'Enhance',
  essential: 'Improve'
};

/// FF2-406 essential is 'biggestGapFirst' not 'worstFirst' (which was mapped to ascending sort - wrong ranking direction)
const sortOrderByDiffCategory = {
  supplementary: smallestGapFirst,
  desirable: smallestGapFirst,
  essential: biggestGapFirst
};

const limitByDiffCategory = {
  supplementary: undefined,
  desirable: undefined,
  essential: undefined
};

// tslint:disable-next-line: interface-over-type-literal
type SkillWithGap = {
  skillName: string;
  skillId: DbKey<Skill>;
  difference: number;
  category: DiffCategory;
};

export function computeDiffCategory(
  qr: QuizResultsHelper,
  by: {
    skillId?: DbKey<Skill>;
    competencyId?: DbKey<Competency>;
    appUserId?: DbKey<AppUser>;
  }
): DiffCategory {
  let localQr = qr;
  if (by.appUserId !== undefined) {
    localQr = localQr.byAppUser(by.appUserId);
  }
  let expected = localQr.expected();
  let actual = localQr.actual();
  if (by.competencyId) {
    expected = localQr.expected({ kind: 'competency', competencyId: by.competencyId });
    actual = localQr.actual({ kind: 'competency', competencyId: by.competencyId });
  }
  if (by.skillId) {
    expected = localQr.expected({ kind: 'skill', skillId: by.skillId });
    actual = localQr.actual({ kind: 'skill', skillId: by.skillId });
  }

  return scoreToDiffCategory(expected, actual);
}

function scoreToDiffCategory(expected: number, actual: number): DiffCategory {
  const scoreTowardsExpected = actual / expected;
  if (scoreTowardsExpected >= 1) {
    return 'supplementary';
  } else if (scoreTowardsExpected > 0.6) {
    return 'desirable';
  } else {
    return 'essential';
  }
}

// TODO: Change DiffCategory to status
export function diffCategoryToStatus(diffCategory: DiffCategory) {
  if (diffCategory === 'essential') {
    return 'improve';
  } else if (diffCategory === 'desirable') {
    return 'enhance';
  } else if (diffCategory === 'supplementary') {
    return 'continue';
  }
  throw Error('unimplemented diffcategory/status mapping');
}

/**
 * Prepare ordered columns of skills by category of skill gap.
 * - Map by DiffCategory
 * - Order by skill gap (different orderings apply)
 * - Limit to N
 */
function makeSkillsByDiffCategory(
  qr: QuizResultsHelper,
  xlat: TranslateFn
): StringKeyMap<DiffCategory, SkillWithGap[]> {
  const skillsByDiffCategory: StringKeyMap<DiffCategory, SkillWithGap[]> = {};

  /// Fetch all skills defined against role
  for (const skillId of qr.getSkillIds()) {
    /// FF2-407 don't display the skill at all if it has no actual scores; ie. no questions have been asked on it
    if (qr.actualScoreForSkillExists(skillId)) {
      const skillDifference = qr.difference({ kind: 'skill', skillId });
      const diffCategory = computeDiffCategory(qr, { skillId });

      skillsByDiffCategory[diffCategory] = skillsByDiffCategory[diffCategory] || [];
      skillsByDiffCategory[diffCategory].push({
        skillId,
        skillName: xlat(qr.skill(skillId).name),
        difference: skillDifference,
        category: diffCategory
      });
    }
  }

  for (const dc of diffCategoryList) {
    // const sortBy = order(
    //   compareByGet<SkillWithGap, number, string>(t => t.difference, t => t.skillName),
    //   sortOrderByDiffCategory[dc]
    // );
    const sortBy = compareByGet<SkillWithGap, number, string>(
      t => t.difference,
      t => t.skillName,
      sortOrderByDiffCategory[dc]
    );
    const limit = limitByDiffCategory[dc];
    skillsByDiffCategory[dc] = skillsByDiffCategory[dc] || [];
    skillsByDiffCategory[dc] = skillsByDiffCategory[dc].sort(sortBy).slice(0, limit);
  }

  return skillsByDiffCategory;
}

/** Props for the multiple competencies bars component */
export interface CompetencyGapReportProps extends WithI18nProps {
  qr: QuizResultsHelper;
}

/**
 * CompetencyGapReport - Component showing areas doing well or needing improving
 */
@observer
export class CompetencyGapReport0 extends React.Component<CompetencyGapReportProps> {
  @observable skillSelected: string | undefined;
  /** Renders the component - new version styled as columns of skill 'cards' */

  @action setSkillSelected = (skillId: string) => {
    this.skillSelected = skillId;
  };

  @action deselectSkill = () => {
    this.skillSelected = undefined;
  };

  renderModal(swg: SkillWithGap, skill: Skill, competency: Competency): JSX.Element {
    const { t } = this.props;
    return (
      <Modal
        isOpen={swg.skillId === this.skillSelected}
        onRequestClose={this.deselectSkill}
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
        key={swg.skillId}
      >
        <div className={styles.title}>{t(skill.name)}</div>
        <div className={styles.competency}>
          <Pill kind={'bordered'}>{t(competency.name).toUpperCase()}</Pill>
        </div>
        <div className={styles.definition}>
          <b>{i18next.t('guidance.definition-title', 'Definition')}</b>
          <p>{t(skill.definition)}</p>
        </div>
        <div className={styles.tips}>
          <MarkdownContent text={t(skill.guidanceTips)} />
        </div>
      </Modal>
    );
  }

  renderTiles(category: string): JSX.Element {
    const skillsByDiffCategory: StringKeyMap<DiffCategory, SkillWithGap[]> = makeSkillsByDiffCategory(
      this.props.qr,
      this.props.t
    );
    const { t } = this.props;

    return (
      <>
        {skillsByDiffCategory[category].map(swg => {
          const skill = this.props.qr.skill(swg.skillId);
          const competency = this.props.qr.competency(skill.competencyId);
          return (
            <div key={swg.skillId}>
              {this.renderModal(swg, skill, competency)}
              <Tile
                kind={'guidance'}
                title={t(skill.name)}
                subtitle={t(competency.name).toUpperCase()}
                detail={t(skill.definition)}
                onClick={() => this.setSkillSelected(swg.skillId)}
                enabled={true}
              />
              <br />
            </div>
          );
        })}
      </>
    );
  }

  renderGuidanceCategory(category: string): JSX.Element {
    return (
      <div className={styles.category} key={category}>
        <GuidanceCategory
          title={i18next.t(`guidance.${category}`, titlesByDiffCategory[category])}
          content={this.renderTiles(category)}
        />
      </div>
    );
  }

  render() {
    return (
      <div className={styles.container}>
        {diffCategoryList.map(dc => {
          return this.renderGuidanceCategory(dc);
        })}
      </div>
    );
  }
}

export const CompetencyGapReport = withI18n(CompetencyGapReport0);



// WEBPACK FOOTER //
// ./src/ui/components/competency-gap-report/competency-gap-report.tsx